import { FC } from "react";

import { HStack, Text, VStack } from "@/components/ui";

import {
  formatMargin,
  formatPercent,
  formatVotes,
  STRENGTH_LABEL,
} from "./colors";
import { type RaceRow, raceVerdict } from "./view";

interface RaceHeadlineProps {
  /** The compared source is the count. */
  results: boolean;
  row: RaceRow;
  sourceLabel: string | null;
}

/** Candidates listed under the count. */
const COUNTS_SHOWN = 5;

const Tile: FC<{ label: string; note: string; value: string }> = ({
  label,
  note,
  value,
}) => (
  <VStack className="flex-1 gap-0.5 rounded-xl bg-background-50 px-4 py-3">
    <Text className="text-xs text-typography-500" numberOfLines={1}>
      {label}
    </Text>
    <Text className="text-xl font-bold text-typography-900">{value}</Text>
    <Text className="text-xs text-typography-500">{note}</Text>
  </VStack>
);

/** A race's headline reads: Foresight next to the compared source, and
    with the count compared, every candidate's votes. */
const RaceHeadline: FC<RaceHeadlineProps> = ({ results, row, sourceLabel }) => {
  const { estimate, external, leftParty } = row;

  if (results) {
    const verdict = raceVerdict(row);
    const result = external?.result ?? null;
    return (
      <VStack className="gap-3">
        <HStack className="gap-3">
          <Tile
            label="Our call"
            note={
              estimate
                ? `call ${verdict.ours ?? "even"}${verdict.miss ? " · missed" : ""}`
                : "Not surveyed"
            }
            value={estimate ? formatMargin(estimate.margin, leftParty) : "—"}
          />
          <Tile
            label="Count"
            note={
              result
                ? `${result.reporting}% in · ${verdict.actual ? `called ${verdict.actual}` : "not called"}`
                : "No count yet"
            }
            value={formatMargin(external?.margin ?? null, leftParty)}
          />
          {verdict.gap !== null && (
            <Tile
              label="Gap"
              note={`Foresight more ${verdict.gap > 0 ? leftParty : "R"}`}
              value={`${Math.abs(verdict.gap).toFixed(1)} pts`}
            />
          )}
        </HStack>
        {result && (
          <VStack className="gap-1">
            {result.counts.slice(0, COUNTS_SHOWN).map((count) => (
              <HStack className="items-center gap-3" key={count.name}>
                <Text
                  className="flex-1 text-sm text-typography-800"
                  numberOfLines={1}
                >
                  {count.name}
                  <Text className="text-xs text-typography-500">
                    {" "}
                    · {count.listed}
                  </Text>
                </Text>
                <Text className="w-28 text-right text-sm font-medium text-typography-900">
                  {formatVotes(count.votes)}
                </Text>
                <Text className="w-14 text-right text-xs text-typography-500">
                  {result.totalVotes > 0
                    ? formatPercent(count.votes / result.totalVotes, 1)
                    : "—"}
                </Text>
              </HStack>
            ))}
            <Text className="text-xs text-typography-400">
              Margins are two-party, like Foresight’s. Count: civicAPI
              (civicapi.org).
            </Text>
          </VStack>
        )}
      </VStack>
    );
  }

  const gap =
    estimate && row.externalProbability !== null
      ? (estimate.leftProbability - row.externalProbability) * 100
      : null;
  return (
    <HStack className="gap-3">
      <Tile
        label="Foresight"
        note={
          estimate
            ? `${formatPercent(estimate.leftProbability)} ${leftParty} win · ${STRENGTH_LABEL[row.call.strength]}`
            : "Not surveyed yet"
        }
        value={estimate ? formatMargin(estimate.margin, leftParty) : "—"}
      />
      {sourceLabel && (
        <Tile
          label={sourceLabel}
          note={
            external?.margin !== null && external?.margin !== undefined
              ? `Margin ${formatMargin(external.margin, leftParty)}`
              : "Win probability"
          }
          value={
            row.externalProbability === null
              ? "—"
              : `${formatPercent(row.externalProbability)} ${leftParty}`
          }
        />
      )}
      {gap !== null && (
        <Tile
          label="Gap"
          note={`Foresight ${gap > 0 ? "more" : "less"} ${leftParty}-leaning`}
          value={`${gap > 0 ? "+" : "−"}${Math.abs(gap).toFixed(0)} pts`}
        />
      )}
    </HStack>
  );
};

export default RaceHeadline;
